import { useEffect, useState, type FormEvent } from "react";
import Button from "../../../shared/components/Button";
import { FloatingWindow } from "../../../shared/components/FloatingWindow";
import { Dropdown, TextField } from "../../../shared/components/form";
import { GroupEditor } from "./GroupEditor";
import { RTSPScannerModal } from "./RTSPScannerModal";
import {
  getWorkflowNodeLogs,
  probeRTSPFeed,
  type WorkflowNodeLog,
} from "../api";
import type { WorkflowNode } from "../model";
import { createGroupGraph, settingsFields, type GroupGraph } from "../model";
import { AudioSpectrum } from "./AudioSpectrum";
import "./NodeConfig.scss";

type NodeConfigProps = {
  node: WorkflowNode;
  hasInput: boolean;
  hasAudioInput: boolean;
  runtimeRunning: boolean;
  upstreamNodes: WorkflowNode[];
  onClose: () => void;
  onSave: (config: Record<string, string>, group?: GroupGraph) => void;
  onApply: (
    config: Record<string, string>,
    group?: GroupGraph,
  ) => Promise<{ logs: WorkflowNodeLog[]; error?: string }>;
};

export function NodeConfig({
  node,
  hasInput,
  hasAudioInput,
  runtimeRunning,
  upstreamNodes,
  onClose,
  onSave,
  onApply,
}: NodeConfigProps) {
  const fields = settingsFields[node.kind];
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      fields.map((field) => [
        field.key,
        node.config[field.key] ?? field.defaultValue ?? "",
      ]),
    ),
  );
  const [groupDraft, setGroupDraft] = useState<GroupGraph>(
    () => node.group ?? createGroupGraph(),
  );
  const [scannerOpen, setScannerOpen] = useState(false);
  const [logs, setLogs] = useState<WorkflowNodeLog[]>([]);
  const rtspSource = node.kind === "camera" ? values.source?.trim() : "";
  const rtspUsername = node.kind === "camera" ? (values.username ?? "") : "";
  const rtspPassword = node.kind === "camera" ? (values.password ?? "") : "";

  useEffect(() => {
    let active = true;
    getWorkflowNodeLogs(node.id)
      .then((nextLogs) => {
        if (active) setLogs(nextLogs);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [node.id]);

  useEffect(() => {
    if (!rtspSource || !rtspSource.toLowerCase().startsWith("rtsp://")) return;
    let active = true;
    probeRTSPFeed(rtspSource, rtspUsername, rtspPassword)
      .then(({ resolution }) => {
        if (active) {
          setValues((current) =>
            current.source?.trim() === rtspSource
              ? { ...current, resolution }
              : current,
          );
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [rtspPassword, rtspSource, rtspUsername]);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave(values, node.kind === "group" ? groupDraft : undefined);
  };
  const apply = async () => {
    try {
      const result = await onApply(
        values,
        node.kind === "group" ? groupDraft : undefined,
      );
      setLogs(result.logs);
    } catch (error) {
      setLogs((current) => [
        ...current,
        {
          timestamp: new Date().toISOString(),
          level: "error",
          message:
            error instanceof Error
              ? error.message
              : "Backend did not respond to configuration apply",
        },
      ]);
    }
  };
  const renderSettings = () => {
    return (
      <div className="settings-list">
        {fields.length > 0 ? (
          fields.map((field) =>
            field.type === "select" || field.type === "multi-select" ? (
              (() => {
                const options = field.options?.includes(values[field.key])
                  ? field.options
                  : [values[field.key], ...(field.options ?? [])].filter(
                      Boolean,
                    );
                return (
                  <Dropdown
                    className="input"
                    key={field.key}
                    label={field.label}
                    withSearch={Boolean(
                      field.options && field.options.length > 5,
                    )}
                    clearable={
                      node.kind === "segment" && field.key === "classes"
                    }
                    multiple={field.type === "multi-select"}
                    value={values[field.key]}
                     onChange={(event) =>
                       setValues((current) => ({
                         ...current,
                         [field.key]: field.type === "multi-select"
                           ? Array.from(event.target.selectedOptions, (option) => option.value).join(", ")
                           : event.target.value,
                       }))
                     }
                  >
                    {options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Dropdown>
                );
              })()
            ) : (
              <TextField
                className="input"
                key={field.key}
                label={field.label}
                max={
                  node.kind === "display" && field.key === "fps"
                    ? "60"
                    : undefined
                }
                min={
                  node.kind === "display" && field.key === "fps"
                    ? "1"
                    : undefined
                }
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    [field.key]: event.target.value,
                  }))
                }
                placeholder={field.placeholder}
                step={
                  node.kind === "display" && field.key === "fps"
                    ? "1"
                    : undefined
                }
                type={field.type ?? "text"}
                value={values[field.key]}
              />
            ),
          )
        ) : (
          <p className="empty-settings">No settings for this section.</p>
        )}
      </div>
    );
  };

  return (
    <>
      <FloatingWindow
        as="form"
        className="node-config-panel"
        onClose={onClose}
        onSubmit={submit}
        subtitle={node.subtitle}
        title={`Config - ${node.title}`}
      >
        <header className="config-summary">
          <span className={`capability capability--${node.capability ?? "live"}`}>
            {node.capability === "planned" ? "Planned" : "Live"}
          </span>
          <span>{node.capability === "planned" ? "Runtime unavailable" : node.status}</span>
          <span>{node.subtitle}</span>
        </header>
        <div className={`config-main${node.kind === "display" ? " config-main--preview" : ""}`}>
          <section className="config-fields" aria-label="Configuration">
            <h3>Configure</h3>
            {renderSettings()}
            {node.kind === "camera" && (
              <Button
                className="scan-network"
                onClick={() => setScannerOpen(true)}
                type="button"
                variant="secondary"
              >
                Scan Network
              </Button>
            )}
          </section>
          {node.kind === "display" && (
            <section className="source-preview">
              <h3>Input Feed</h3>
              {runtimeRunning && hasInput ? (
                <img
                  src={`/api/workflow/runtime/outputs/${node.id}/stream?fps=${Math.min(60, Math.max(1, Number(values.fps ?? "1") || 1))}`}
                  alt={`${node.title} preview`}
                />
              ) : (
                <div className="preview-empty">No live input</div>
              )}
              {hasAudioInput && <AudioSpectrum nodeID={node.id} />}
            </section>
          )}
          {node.kind === "group" && <GroupEditor group={groupDraft} onChange={setGroupDraft} />}
        </div>
        <details className="config-details">
          <summary>Connections</summary>
          <dl className="connection-list">
            <div><dt>Input</dt><dd>{node.input}</dd></div>
            <div><dt>Output</dt><dd>{node.output}</dd></div>
            {upstreamNodes.map((upstreamNode) => (
              <div key={upstreamNode.id}><dt>{upstreamNode.title}</dt><dd>{upstreamNode.output}</dd></div>
            ))}
          </dl>
        </details>
        <details className="config-details" open={logs.some((log) => log.level === "error")}>
          <summary>Backend Logs <span>{logs.length}</span></summary>
          {logs.length > 0 ? (
            <ol>
              {logs.map((log, index) => (
                <li
                  className={`node-log node-log--${log.level}`}
                  key={`${log.timestamp}-${index}`}
                >
                  <time dateTime={log.timestamp}>
                    {new Date(log.timestamp).toLocaleString()}
                  </time>
                  <span>{log.message}</span>
                </li>
              ))}
            </ol>
          ) : (
            <p>No backend activity yet.</p>
          )}
        </details>
        <div className="actions">
          <Button className="cancel" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button className="apply" onClick={apply} type="button">
            Apply
          </Button>
          <Button className="save" type="submit">
            Save
          </Button>
        </div>
      </FloatingWindow>
      {scannerOpen && (
        <RTSPScannerModal
          onClose={() => setScannerOpen(false)}
          onSelect={(source) => {
            setValues((current) => ({ ...current, source }));
            setScannerOpen(false);
          }}
        />
      )}
    </>
  );
}
